<template>
  <div class="tml-form">
    <!-- @slot Should contain all form inputs. -->
    <slot />
  </div>
</template>

<script>
import {forms} from '@teemill/common/plugins';

/**
 * @deprecated Please use TmlNewForm instead.
 *
 * TmlForms are used as a controller for all Tml inputs.
 * All Tml input components MUST be wrapped in a TmlForm else they will not function correctly.
 */
export default {
  name: 'TmlForm',

  provide() {
    return {
      submitForm: this.submitForm,
      registerField: this.onRegisterField,
      initialiseFieldObject: this.onInitialiseFieldObject,
      removeFieldObject: this.onRemoveFieldObject,
      setFieldState: this.onSetFieldState,
      setFieldProperty: this.onSetFieldProperty,
      getFieldProperty: this.onGetFieldProperty,
    };
  },

  props: {
    /**
     * Unique name of the form.
     */
    formName: String,
    persistent: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      fieldList: [],
    };
  },

  created() {
    forms.reply(`form-validate-${this.formName}`, this.validateAll);

    if (!this.$store.state.forms) {
      this.$store.registerModule('forms', {namespaced: true, state: {}});
    }

    if (typeof this.$store.state.forms[this.formName] === 'undefined') {
      this.$store.registerModule(['forms', this.formName], {
        namespaced: true,
        state: {},
        getters: {
          isValid(state) {
            const keys = Object.keys(state);
            for (let i = 0; i < keys.length; i++) {
              const field = state[keys[i]];
              if (!field.isValid) {
                return false;
              }
            }
            return true;
          },
          formData(state) {
            return state;
          },
          getFieldValue: state => (fieldName, propertyName) =>
            state[fieldName] ? state[fieldName][propertyName] : null,
        },
        mutations: {
          onRegisterField(state, data) {
            state[data.fieldName] = data.fieldData;
          },
          onUnregisterField(state, data) {
            delete state[data.fieldName];
          },
          onSetFieldState(state, data) {
            state[data.fieldName] = data.fieldData;
          },
          onSetFieldValue(state, data) {
            if (state[data.fieldName]) {
              state[data.fieldName][data.propertyName] = data.propertyValue;
            }
          },
          reset(state) {
            Object.values(state).forEach(field => {
              field.value = null;
              field.isValid = false;
            });
          },
          unload(state) {
            Object.keys(state).forEach(key => {
              delete state[key];
            });
          },
        },
      });
    }
  },

  /**
   * Prevent re-registration of modules when forms
   * are re-created/mounted eg. feedbackPopup
   */
  unmounted() {
    if (!this.persistent && this.$store.state.forms[this.formName]) {
      this.$store.unregisterModule(['forms', this.formName]);
    }
  },

  methods: {
    onRegisterField(fieldName, fieldData) {
      if (typeof this.$store.state.forms[this.formName] !== 'undefined') {
        if (
          typeof this.$store.state.forms[this.formName][fieldName] ===
          'undefined'
        ) {
          this.$store.commit(`forms/${this.formName}/onRegisterField`, {
            fieldName,
            fieldData,
          });
        }
      }
    },

    onInitialiseFieldObject(field) {
      this.fieldList.push(field);
    },

    onRemoveFieldObject(field) {
      this.fieldList = this.fieldList.filter(
        field2 => field.fieldName !== field2.fieldName
      );
    },

    onSetFieldState(fieldName, fieldData) {
      this.$store.commit(`forms/${this.formName}/onSetFieldState`, {
        fieldName,
        fieldData,
      });
    },

    onSetFieldProperty(fieldName, propertyName, propertyValue) {
      this.$store.commit(`forms/${this.formName}/onSetFieldValue`, {
        fieldName,
        propertyName,
        propertyValue,
      });
    },

    onGetFieldProperty(fieldName, propertyName) {
      const getFieldProperty =
        this.$store.getters[`forms/${this.formName}/getFieldValue`];
      if (typeof getFieldProperty === 'function') {
        return getFieldProperty(fieldName, propertyName);
      }
      return null;
    },

    validateAll() {
      return new Promise(resolve => {
        let successful = 0;
        let completed = 0;
        const l = this.fieldList.length;

        if (l === 0) {
          this.$emit('validated');

          resolve(true);

          return;
        }

        const callbackFn = success => {
          completed += 1;
          if (success) {
            successful += 1;
          }
          if (completed === l) {
            this.$emit('validated');

            resolve(successful === completed);
          }
        };
        for (let i = 0; i < l; ++i) {
          const field = this.fieldList[i];
          if (field.validationRules) {
            field.validate(
              this.onGetFieldProperty(field.fieldName, 'value'),
              callbackFn
            );
          } else {
            callbackFn(true);
          }
        }
      });
    },
  },
};
</script>
